import { ConfigurationSourceDataMap, CoordinatingGroup, Institution, MasterGroup, Metadata, Participant, SourceData, SourceDataFieldType, Trial } from "@/api/dtos";
import { useMemo } from "react";
import { createItemGroupConfiguration } from "../configuration/useItemGroupConfiguration";
import { createItemConfiguration } from "../configuration/useItemConfiguration";
import { createItem } from "./useItem";

export interface ISourceDataItemGroupGrid {
    label?: string;
    columns?: ISourceDataItemGroupGridColumn[];
    rows?: ISourceDataItemGroupGridRow[];
}

export interface ISourceDataItemGroupGridColumn {
    label?: string;
}

export interface ISourceDataItemGroupGridRow {
    repeat?: number;
    cells?: Array<NonNullable<ReturnType<typeof createItem>>>;
}

const useSourceDataItemGroupGrid = (
    metadata?: Metadata, 
    trialConfiguration?: ConfigurationSourceDataMap, 
    configuration?: ConfigurationSourceDataMap, 
    sourceData?: SourceData,
    trial?: Trial, 
    masterGroup?: MasterGroup,
    coordinatingGroup?: CoordinatingGroup,
    institution?: Institution,
    participant?: Participant,
    studyEventOid?: string,
    studyEventRepeat?: number,
    caseReportFormOid?: string,
    caseReportFormVersionOid?: string,
    itemGroupName?: string,
    preview?: boolean
) => {
    return useMemo(() => {
        if (!metadata || !metadata.events) {
            return undefined;
        }

        if (
            !studyEventOid ||
            !studyEventRepeat ||
            !caseReportFormOid ||
            !caseReportFormVersionOid ||
            !itemGroupName ||
            !metadata || 
            (studyEventOid && !metadata.eventForms?.[studyEventOid]?.[caseReportFormOid]) ||
            !metadata.items?.[caseReportFormOid]?.[caseReportFormVersionOid]?.[itemGroupName]
        ) {
            return undefined;
        }

        const itemGroupConfiguration = createItemGroupConfiguration(
            metadata, 
            trialConfiguration?.itemGroups, 
            configuration?.itemGroups, 
            trial, 
            configuration?.configuration?.isGlobal,
            configuration?.configuration?.id, 
            studyEventOid,
            caseReportFormOid,
            caseReportFormVersionOid,
            itemGroupName
        );
        
        if (!itemGroupConfiguration) {
            return undefined;
        }

        const itemsConfigurations = Object.keys(metadata.items![caseReportFormOid][caseReportFormVersionOid][itemGroupName]).map((itemName) => {
                            const item = createItemConfiguration(
                                metadata, 
                                trialConfiguration?.items, 
                                configuration?.items, 
                                trial, 
                                configuration?.configuration?.isGlobal,
                                configuration?.configuration?.id,  
                                studyEventOid,
                                caseReportFormOid,
                                caseReportFormVersionOid,
                                itemGroupName,
                                itemName
                            );
        
                            if (!item) {
                                return undefined;
                            }

                            if (item.itemMetadata?.fieldType === SourceDataFieldType.Hidden) {
                                return undefined;
                            }

                            return item
                        })
                        .filter(x => !!x)
                        .sort((a, b) => (a?.itemMetadata?.order ?? 0) < (b?.itemMetadata?.order ?? 0) ? -1 : 1)
                        
                        
        const sourceDataItemGroups = sourceData?.items?.[studyEventOid]?.[studyEventRepeat]?.[caseReportFormOid]?.[itemGroupName];

        const sourceDataItemGroupRepeats = Object.keys(sourceDataItemGroups ?? {});


        return {
            label: itemGroupConfiguration.displayName,
            columns: itemsConfigurations.map(x => ({
                label: x?.displayName,
                fieldType: x?.itemMetadata?.fieldType
            } as ISourceDataItemGroupGridColumn)),
            rows: sourceDataItemGroupRepeats.map(x => ({
                repeat: +x,
                cells: itemsConfigurations.map(itemConfiguration => createItem(
                    metadata, 
                    trialConfiguration?.items, 
                    configuration?.items,
                    sourceData?.items,
                    trialConfiguration?.itemValues, 
                    configuration?.itemValues,
                    trial, 
                    masterGroup,
                    coordinatingGroup,
                    institution,
                    participant,
                    configuration?.configuration?.isGlobal,
                    configuration?.configuration?.id, 
                    studyEventOid,
                    studyEventRepeat,
                    caseReportFormOid,
                    caseReportFormVersionOid,
                    itemGroupName,
                    +x,
                    itemConfiguration?.itemName,
                    preview
                ))
            } as ISourceDataItemGroupGridRow))
        } as ISourceDataItemGroupGrid

    }, [
        metadata, 
        trialConfiguration?.items, 
        configuration?.items, 
        trialConfiguration?.itemGroups, 
        configuration?.itemGroups, 
        sourceData?.items, 
        trialConfiguration?.itemValues, 
        configuration?.itemValues,
        trial, 
        institution,
        masterGroup, 
        coordinatingGroup, 
        participant,
        studyEventOid,
        studyEventRepeat,
        caseReportFormOid,
        caseReportFormVersionOid,
        itemGroupName,
        preview
    ]) as ISourceDataItemGroupGrid | undefined
};

export default useSourceDataItemGroupGrid;